import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Modal,
  IconButton,
  useTheme,
  Pagination,
  Stack,
} from "@mui/material";
import {
  DirectionsBus,
  Event,
  Schedule,
  Paid,
  CurrencyRupee,
  Person,
  PictureAsPdf
} from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";
import BusTicketInvoice from "./BusTicketInvoice"; // New component

const BusTransactionReport = () => {
  const theme = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10, // Default page size
    hasNext: false,
    hasPrevious: false
  });

  useEffect(() => {
    fetchData(pagination.currentPage, pagination.pageSize);
  }, [pagination.currentPage, pagination.pageSize]);

  const fetchData = async (page, size) => {
    const token = sessionStorage.getItem("jwtToken");
    try {
      const response = await axios.get(
        "https://api.ipaisa.co.in/myapp/bus/busbookingtransactions",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: page,
            size: size
          }
        }
      );
      
      if(response.status === 200) {
        setTransactions(response.data.transactions);
        setPagination({
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalItems: response.data.pagination.totalItems,
          pageSize: size,
          hasNext: response.data.pagination.hasNext,
          hasPrevious: response.data.pagination.hasPrevious
        });
      } else {
        setTransactions([]);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    // MUI Pagination is 1-indexed, our API is 0-indexed
    setPagination(prev => ({ ...prev, currentPage: newPage - 1 }));
  };

  const handlePageSizeChange = (event) => {
    setPagination(prev => ({ 
      ...prev, 
      pageSize: parseInt(event.target.value, 10),
      currentPage: 0 // Reset to first page when page size changes
    }));
  };


  const handleCancelClick = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenModal(true);
  };

  const handleInvoiceClick = (transaction) => {
    setInvoiceData(transaction);
    setShowInvoice(true);
  };

  const handleCloseInvoice = () => {
    setShowInvoice(false);
  };

  const handleConfirmCancel = async () => {
    const token = sessionStorage.getItem("jwtToken");
    try {
      const response = await axios.post(
        "https://api.ipaisa.co.in/myapp/bus/cancel",
        {
          BusId: selectedTransaction.busId,
          TraceId: selectedTransaction.traceId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.data.status === "success") {
        Swal.fire({
          icon: "success",
          title: "Cancellation Successful",
          text: response.data.message,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Cancellation Failed",
          text: response.data.message,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setOpenModal(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error: {error}</Typography>;

  return (
    <Box sx={{ padding: "2rem", marginTop: "3%" }}>
      <Grid item xs={12} md={12}>
        {/* Page size selector */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Typography variant="body2" sx={{ mr: 1, alignSelf: 'center' }}>
            Items per page:
          </Typography>
          <select 
            value={pagination.pageSize}
            onChange={handlePageSizeChange}
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper
            }}
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </Box>

        <TableContainer
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            background: "white",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
          }}
        >
        <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: "linear-gradient(45deg, #0152a9, #4a90e2)",
                }}
              >
                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                  <DirectionsBus sx={{ verticalAlign: "middle", mr: 1 }} />
                  Bus Details
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                  <Event sx={{ verticalAlign: "middle", mr: 1 }} />
                  Journey
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                  <Schedule sx={{ verticalAlign: "middle", mr: 1 }} />
                  Timings
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                  <CurrencyRupee sx={{ verticalAlign: "middle", mr: 1 }} />
                  Payment
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                  <Person sx={{ verticalAlign: "middle", mr: 1 }} />
                  Passenger
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                  Invoice
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length > 0 ? (
                transactions.map((transaction) => (
                  <TableRow
                    key={transaction.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography sx={{fontSize:'14px', fontWeight: "bold" }}>
                        {transaction.busType}
                      </Typography>
                      <Typography sx={{fontSize:'14px'}}>
                        Seat: {transaction.seatName} ({transaction.seatType})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{fontSize:'14px', fontWeight: "bold" }}>
                        {transaction.origin} → {transaction.destination}
                      </Typography>
                      <Typography sx={{fontSize:'14px'}}>
                        {transaction.dateOfJourney}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{fontSize:'14px', fontWeight: "bold" }}>
                        Departure: {transaction.departureTime}
                      </Typography>
                      <Typography sx={{fontSize:'14px', fontWeight: "bold" }}>
                        Arrival: {transaction.arrivalTime}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{fontSize:'14px', fontWeight: "bold" }}>
                        ₹{transaction.invoiceAmount}
                      </Typography>
                      <Typography sx={{fontSize:'14px'}}>
                        {transaction.invoiceNo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{fontSize:'14px', fontWeight: "bold" }}>
                        {transaction.buspassengerfname}
                      </Typography>
                      <Typography sx={{fontSize:'14px'}}>
                        {transaction.contactNo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={() => handleInvoiceClick(transaction)}
                        color="primary"
                      >
                        <PictureAsPdf />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleCancelClick(transaction)}
                        sx={{fontSize:'10px'}}
                      >
                        Cancel
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                      No transactions available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination controls */}
        <Stack spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage + 1} // MUI Pagination is 1-indexed
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2">
            Showing {transactions.length} of {pagination.totalItems} transactions
          </Typography>
        </Stack>
      </Grid>

      {/* Confirmation Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Confirm Cancellation
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to cancel this booking?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleCloseModal} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmCancel}>
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Invoice Modal */}
      {showInvoice && (
        <Modal open={showInvoice} onClose={handleCloseInvoice}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: 800,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <BusTicketInvoice data={invoiceData} onClose={handleCloseInvoice} />
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default BusTransactionReport;
